import axios from "axios";
import { getApiUrl } from "@/helpers";

export interface PrinterHealth {
	name: string;
	status: string;
	isOnline: boolean;
	monitorHealth: boolean;
}

export interface EzeepHealthState {
	status: "healthy" | "degraded" | "error" | "unknown";
	printers: PrinterHealth[];
	totalPrinters: number;
	onlinePrinters: number;
	lastChecked: string | null;
	errorMessage: string | null;
	polling: boolean;
}

const state: EzeepHealthState = {
	status: "unknown",
	printers: [],
	totalPrinters: 0,
	onlinePrinters: 0,
	lastChecked: null,
	errorMessage: null,
	polling: false,
};

let pollTimer: ReturnType<typeof setInterval> | null = null;

const mutations = {
	SET_HEALTH(
		state: EzeepHealthState,
		payload: {
			status: EzeepHealthState["status"];
			printers: PrinterHealth[];
			totalPrinters: number;
			onlinePrinters: number;
			errorMessage?: string | null;
		}
	) {
		state.status = payload.status;
		state.printers = payload.printers;
		state.totalPrinters = payload.totalPrinters;
		state.onlinePrinters = payload.onlinePrinters;
		state.errorMessage = payload.errorMessage || null;
		state.lastChecked = new Date().toISOString();
	},
	SET_ERROR(state: EzeepHealthState, message: string) {
		state.status = "error";
		state.errorMessage = message;
		state.lastChecked = new Date().toISOString();
	},
	SET_POLLING(state: EzeepHealthState, polling: boolean) {
		state.polling = polling;
	},
};

const actions = {
	async checkHealth({ commit }: { commit: Function }) {
		try {
			const response = await axios.get(getApiUrl("/api/print/printers"), {
				withCredentials: true,
			});

			const data = response.data;
			const allPrinters: PrinterHealth[] = (data.printers || data || []).map(
				(p: any) => ({
					name: p.name,
					status: p.status || "Unknown",
					isOnline: p.status === "Online",
					monitorHealth: p.monitorHealth !== false,
				})
			);

			// Only consider printers with monitorHealth enabled for status
			const printers = allPrinters;
			const monitored = allPrinters.filter((p) => p.monitorHealth);
			const total = monitored.length;
			const online = monitored.filter((p) => p.isOnline).length;

			let status: EzeepHealthState["status"];
			if (total === 0) {
				status = "error";
			} else if (online === total) {
				status = "healthy";
			} else if (online > 0) {
				status = "degraded";
			} else {
				status = "error";
			}

			commit("SET_HEALTH", {
				status,
				printers,
				totalPrinters: total,
				onlinePrinters: online,
				errorMessage:
					total === 0
						? "No printers configured"
						: online === 0
						? "All printers offline"
						: null,
			});
		} catch (error: any) {
			commit("SET_ERROR", "Unable to reach print service");
		}
	},

	startPolling({ commit, dispatch }: { commit: Function; dispatch: Function }) {
		if (pollTimer) return;
		commit("SET_POLLING", true);

		// Initial check
		dispatch("checkHealth");

		// Poll every 60 seconds
		pollTimer = setInterval(() => {
			dispatch("checkHealth");
		}, 60000);
	},

	stopPolling({ commit }: { commit: Function }) {
		if (pollTimer) {
			clearInterval(pollTimer);
			pollTimer = null;
		}
		commit("SET_POLLING", false);
	},
};

const getters = {
	ezeepStatus: (state: EzeepHealthState) => state.status,
	ezeepIsHealthy: (state: EzeepHealthState) => state.status === "healthy",
	ezeepHasIssues: (state: EzeepHealthState) =>
		state.status === "degraded" || state.status === "error",
	ezeepStatusColor: (state: EzeepHealthState) => {
		switch (state.status) {
			case "healthy":
				return "success";
			case "degraded":
				return "warning";
			case "error":
				return "error";
			default:
				return "grey";
		}
	},
	ezeepStatusMessage: (state: EzeepHealthState) => {
		switch (state.status) {
			case "healthy":
				return `${state.onlinePrinters}/${state.totalPrinters} printers online`;
			case "degraded":
				return `${state.onlinePrinters}/${state.totalPrinters} printers online`;
			case "error":
				return state.errorMessage || "Print service unavailable";
			default:
				return "Checking printer status...";
		}
	},
	ezeepPrinters: (state: EzeepHealthState) => state.printers,
	ezeepLastChecked: (state: EzeepHealthState) => state.lastChecked,
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
};
